import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import {
  Truck, RefreshCw, Upload, ChevronDown, Package, Save, Trash2, Calendar, Check, X, Download,
} from 'lucide-react';
import clsx from 'clsx';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

const fmtDatum = (s: any) => {
  if (!s) return '—';
  const d = new Date(String(s));
  if (isNaN(d.getTime())) return String(s);
  const p = (n: number) => String(n).padStart(2, '0');
  return p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + d.getFullYear();
};

const statusCls = (s: string) => {
  const m: Record<string, string> = {
    uvozeno: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    potrjeno: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
    napaka: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  };
  return m[s] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
};

const VRSTA_LABEL: Record<string, string> = {
  vozicki_rlc: 'Vozički RLC', palete: 'Palete', box: 'Box', drugo: 'Drugo (prejem)',
};

const saldoCls = (n: any) => {
  if (n === null || n === undefined) return 'text-gray-400';
  if (n < 0) return 'text-red-600 dark:text-red-400 font-semibold';
  if (n > 0) return 'text-emerald-600 dark:text-emerald-400 font-semibold';
  return 'text-gray-500';
};

const poljeCls = (conf: any, vrednost: any) => {
  const prazno = vrednost === null || vrednost === undefined || vrednost === '';
  const neberljivo = String(vrednost).toLowerCase() === 'neberljivo';
  if (neberljivo) return 'border-red-400 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300';
  if (prazno) return 'border-gray-300 dark:border-gray-600';
  if (conf === null || conf === undefined) return 'border-gray-300 dark:border-gray-600';
  if (conf >= 0.85) return 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30';
  if (conf >= 0.60) return 'border-amber-300 bg-amber-50 dark:bg-amber-950/30';
  return 'border-red-400 bg-red-50 dark:bg-red-950/40';
};

type GrupaKljuc = 'voznik' | 'filiala' | 'datum';
const GRUPE: { k: GrupaKljuc; label: string }[] = [
  { k: 'voznik', label: 'Šofer' },
  { k: 'filiala', label: 'Filiala' },
  { k: 'datum', label: 'Datum' },
];
const PRIVZETI_RED: GrupaKljuc[] = ['voznik', 'filiala', 'datum'];

// ── Datumski filter ──
const isoDan = (d: Date) =>
  d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
type DatumMode = 'vse' | 'tekoci' | 'zadnji' | 'po_meri';
const obdobjeZaMode = (mode: DatumMode, od: string, doo: string): [string, string] | null => {
  const danes = new Date();
  if (mode === 'tekoci')
    return [isoDan(new Date(danes.getFullYear(), danes.getMonth(), 1)),
            isoDan(new Date(danes.getFullYear(), danes.getMonth() + 1, 0))];
  if (mode === 'zadnji')
    return [isoDan(new Date(danes.getFullYear(), danes.getMonth() - 1, 1)),
            isoDan(new Date(danes.getFullYear(), danes.getMonth(), 0))];
  if (mode === 'po_meri') {
    if (!od && !doo) return null;
    return [od || '0000-01-01', doo || '9999-12-31'];
  }
  return null;
};

interface Saldo3 { vozicki_rlc: number; palete: number; box: number; }
interface SaldoAgg { zacetno: Saldo3; dostava: Saldo3; vracilo: Saldo3; koncno: Saldo3; }
interface Vrstica {
  transport_id: string; st_prevoznice: string; datum: string | null;
  voznik: string | null; voznik_tel: string | null; reg_st: string | null;
  status: string | null; uvozeno: string | null;
  filiala: string; vracilo_id: string | null;
  vracilo: { vozicki_rlc: string | null; palete: string | null; box: string | null };
  ocr_confidence: number | null;
  dostava: { vozicki_rlc: number; palete: number; box: number; drugo: number; drugo_razpis?: Record<string, number> };
  prevzem: Saldo3; saldo_poti: Saldo3; skupni_saldo: Saldo3;
}

const kljucVrstice = (v: Vrstica) => v.transport_id + '__' + v.filiala;
const sortVrednost = (v: Vrstica, k: GrupaKljuc) =>
  k === 'voznik' ? (v.voznik || '') : k === 'filiala' ? (v.filiala || '') : (v.datum || '');
const grupaLabel = (v: Vrstica, k: GrupaKljuc) =>
  k === 'datum' ? fmtDatum(v.datum) : (sortVrednost(v, k) || '—');

const SaldoMini = ({ s }: { s: Saldo3 }) => (
  <span className="inline-flex gap-2 text-xs whitespace-nowrap">
    <span className={saldoCls(s.vozicki_rlc)} title="Vozički RLC">V {s.vozicki_rlc}</span>
    <span className={saldoCls(s.palete)} title="Palete">P {s.palete}</span>
    <span className={saldoCls(s.box)} title="Box">B {s.box}</span>
  </span>
);

// povzetek salda za izbrano obdobje: zacetno / dostava / vracilo / koncno
function SaldoPovzetek({ agg, naslov }: { agg: SaldoAgg; naslov: string }) {
  const vrste: ('vozicki_rlc' | 'palete' | 'box')[] = ['vozicki_rlc', 'palete', 'box'];
  return (
    <div>
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
        {naslov}
      </div>
      <table className="text-xs">
        <thead>
          <tr className="text-gray-400">
            <th className="text-left font-normal pr-4 py-0.5">Vrsta</th>
            <th className="text-right font-normal px-3 py-0.5">Začetno</th>
            <th className="text-right font-normal px-3 py-0.5">Dostava</th>
            <th className="text-right font-normal px-3 py-0.5">Vračilo</th>
            <th className="text-right font-normal px-3 py-0.5">Končno</th>
          </tr>
        </thead>
        <tbody>
          {vrste.map((k) => (
            <tr key={k}>
              <td className="text-left pr-4 py-0.5 text-gray-600 dark:text-gray-300">{VRSTA_LABEL[k]}</td>
              <td className={clsx('text-right px-3 py-0.5', saldoCls(agg.zacetno[k]))}>{agg.zacetno[k]}</td>
              <td className="text-right px-3 py-0.5 text-gray-600 dark:text-gray-300">{agg.dostava[k]}</td>
              <td className="text-right px-3 py-0.5 text-gray-600 dark:text-gray-300">{agg.vracilo[k]}</td>
              <td className={clsx('text-right px-3 py-0.5', saldoCls(agg.koncno[k]))}>{agg.koncno[k]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// info o skenu: kratkozivni token + stevilo strani
function useSkenInfo(baza: any, transportId: string) {
  const [info, setInfo] = useState<{ token: string; strani: number } | null>(null);
  useEffect(() => {
    let ziv = true;
    setInfo(null);
    api.get('/logistika/' + baza.id + '/transport/' + transportId + '/obrazec-info')
      .then((r: any) => { if (ziv) setInfo({ token: r.data.token, strani: r.data.strani || 1 }); })
      .catch(() => { if (ziv) setInfo(null); });
    return () => { ziv = false; };
  }, [baza, transportId]);
  return info;
}

// prikaz skena kot SLIKE (hitro, brez PDF pregledovalnika) + povezava do PDF
function SkenPanel({ baza, transportId }: { baza: any; transportId: string }) {
  const info = useSkenInfo(baza, transportId);
  const osnova = API_BASE + '/logistika/' + baza.id + '/transport/' + transportId;
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-auto h-[60vh] relative">
      {!info ? (
        <div className="flex items-center justify-center h-full text-gray-400 text-sm">Nalagam sken …</div>
      ) : (
        <>
          {Array.from({ length: info.strani }, (_, i) => i + 1).map((n) => (
            <img key={n}
              src={osnova + '/obrazec-img?stran=' + n + '&token=' + info.token}
              className="w-full block border-b border-gray-100 dark:border-gray-700"
              alt={'Stran ' + n} loading="eager" />
          ))}
          <a href={osnova + '/obrazec-public?token=' + info.token} target="_blank" rel="noreferrer"
            className="absolute top-2 right-2 px-2 py-1 rounded-md bg-gray-900/70 text-white text-xs hover:bg-gray-900">
            Prenesi PDF
          </a>
        </>
      )}
    </div>
  );
}

// potrditev cele prevoznice (transporta)
function PotrdiPrevoznico({ baza, transportId, status, onDone }:
  { baza: any; transportId: string; status: string | null; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const potrjeno = status === 'potrjeno';
  const potrdi = () => {
    setBusy(true);
    api.post('/logistika/' + baza.id + '/transport/' + transportId + '/potrdi')
      .then(() => { toast.success('Prevoznica potrjena'); onDone(); })
      .catch((e: any) => toast.error(String(e?.response?.data?.detail || 'Napaka pri potrjevanju')))
      .finally(() => setBusy(false));
  };
  return (
    <button onClick={potrdi} disabled={busy || potrjeno}
      className={clsx('px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 whitespace-nowrap',
        potrjeno
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300 cursor-default'
          : 'bg-emerald-600 text-white hover:bg-emerald-700')}>
      <Check className="w-4 h-4" /> {potrjeno ? 'Potrjeno' : 'Potrdi prevoznico'}
    </button>
  );
}

// izbris cele prevoznice z modalom za potrditev
function IzbrisiPrevoznico({ baza, transportId, stPrevoznice, onDeleted }:
  { baza: any; transportId: string; stPrevoznice: string; onDeleted: () => void }) {
  const [odprt, setOdprt] = useState(false);
  const [busy, setBusy] = useState(false);
  const izbrisi = () => {
    setBusy(true);
    api.delete('/logistika/' + baza.id + '/transport/' + transportId)
      .then(() => { toast.success('Prevoznica izbrisana'); setOdprt(false); onDeleted(); })
      .catch((e: any) => toast.error(String(e?.response?.data?.detail || 'Napaka pri brisanju')))
      .finally(() => setBusy(false));
  };
  return (
    <>
      <button onClick={() => setOdprt(true)}
        className="px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 whitespace-nowrap
          bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-950/40 dark:text-red-400 dark:hover:bg-red-950/70">
        <Trash2 className="w-4 h-4" /> Izbriši
      </button>
      {odprt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => { if (!busy) setOdprt(false); }}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Izbriši prevoznico?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">
              Prevoznica <span className="font-medium">{stPrevoznice}</span> bo trajno izbrisana —
              z vsemi vračili, dostavami in skenom vred. Tega ni mogoče razveljaviti.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setOdprt(false)} disabled={busy}
                className="px-4 py-2 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600">
                Prekliči
              </button>
              <button onClick={izbrisi} disabled={busy}
                className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 flex items-center gap-1.5 disabled:opacity-60">
                <Trash2 className="w-4 h-4" /> {busy ? 'Brišem …' : 'Izbriši'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// urejanje ene filiale (vracilo + saldo), brez PDF
function FilialaUrejanje({ baza, vrstica, onSaved, prikaziNaslov }:
  { baza: any; vrstica: Vrstica; onSaved: () => void; prikaziNaslov?: boolean }) {
  const [ur, setUr] = useState({
    vozicki_rlc: vrstica.vracilo.vozicki_rlc || '',
    palete: vrstica.vracilo.palete || '',
    box: vrstica.vracilo.box || '',
  });
  const [dirty, setDirty] = useState(false);
  const [shranjujem, setShranjujem] = useState(false);

  useEffect(() => {
    setUr({
      vozicki_rlc: vrstica.vracilo.vozicki_rlc || '',
      palete: vrstica.vracilo.palete || '',
      box: vrstica.vracilo.box || '',
    });
    setDirty(false);
  }, [vrstica.transport_id, vrstica.filiala, vrstica.vracilo_id,
      vrstica.vracilo.vozicki_rlc, vrstica.vracilo.palete, vrstica.vracilo.box]);

  const polje = (k: 'vozicki_rlc' | 'palete' | 'box', val: string) => {
    setUr((p) => ({ ...p, [k]: val })); setDirty(true);
  };
  const shrani = () => {
    setShranjujem(true);
    const body = { filiala: vrstica.filiala, ...ur };
    const p = vrstica.vracilo_id
      ? api.put('/logistika/' + baza.id + '/vracilo/' + vrstica.vracilo_id, body)
      : api.post('/logistika/' + baza.id + '/vracilo', { transport_id: vrstica.transport_id, ...body });
    p.then(() => { toast.success('Shranjeno'); setDirty(false); onSaved(); })
      .catch(() => toast.error('Napaka pri shranjevanju'))
      .finally(() => setShranjujem(false));
  };
  const izbrisi = () => {
    if (!vrstica.vracilo_id) return;
    api.delete('/logistika/' + baza.id + '/vracilo/' + vrstica.vracilo_id)
      .then(() => { toast.success('Vračilo izbrisano'); onSaved(); })
      .catch(() => toast.error('Napaka pri brisanju'));
  };
  const vrsteSalda: ('vozicki_rlc' | 'palete' | 'box')[] = ['vozicki_rlc', 'palete', 'box'];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
      {prikaziNaslov && (
        <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2 flex items-center gap-1.5">
          <Package className="w-4 h-4 text-gray-400" /> {vrstica.filiala}
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
          {!prikaziNaslov && <Package className="w-4 h-4 text-gray-400" />} Vrnjena embalaža (uredi)
        </span>
        <div className="flex items-center gap-1.5">
          <button onClick={shrani} disabled={!dirty || shranjujem}
            className={clsx('px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1',
              dirty ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed')}>
            <Save className="w-3.5 h-3.5" /> Shrani
          </button>
          {vrstica.vracilo_id && (
            <button onClick={izbrisi} title="Izbriši vračilo"
              className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-1">
        {vrsteSalda.map((k) => (
          <div key={k}>
            <label className="block text-xs text-gray-400 mb-0.5">{VRSTA_LABEL[k]}</label>
            <input value={ur[k]} onChange={(e) => polje(k, e.target.value)}
              className={clsx('w-full px-2 py-1 rounded border text-sm dark:bg-gray-700 dark:text-white',
                poljeCls(vrstica.ocr_confidence, ur[k]))} />
          </div>
        ))}
      </div>
      {vrstica.ocr_confidence != null && (
        <div className="text-xs text-gray-400 mb-3">
          Zaupanje OCR: {Math.round(vrstica.ocr_confidence * 100)}%
          {vrstica.ocr_confidence < 0.85 && <span className="text-amber-500 ml-1">— preverite</span>}
        </div>
      )}
      {!vrstica.vracilo_id && (
        <div className="text-xs text-amber-500 mb-3">Za to filialo ni zapisa vračila — shranjevanje ga ustvari.</div>
      )}

      <div className="mt-2 border-t border-gray-100 dark:border-gray-700 pt-2">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-400 border-b border-gray-100 dark:border-gray-700">
              <th className="text-left font-normal py-1">Vrsta</th>
              <th className="text-right font-normal py-1">Dostava</th>
              <th className="text-right font-normal py-1">Prevzem</th>
              <th className="text-right font-normal py-1">Saldo poti</th>
              <th className="text-right font-normal py-1">Skupni saldo</th>
            </tr>
          </thead>
          <tbody>
            {vrsteSalda.map((k) => (
              <tr key={k} className="border-b border-gray-50 dark:border-gray-800/60">
                <td className="py-1 text-gray-700 dark:text-gray-300">{VRSTA_LABEL[k]}</td>
                <td className="py-1 text-right text-gray-600 dark:text-gray-300">{vrstica.dostava[k]}</td>
                <td className="py-1 text-right text-gray-600 dark:text-gray-300">{vrstica.prevzem[k]}</td>
                <td className={clsx('py-1 text-right', saldoCls(vrstica.saldo_poti[k]))}>{vrstica.saldo_poti[k]}</td>
                <td className={clsx('py-1 text-right', saldoCls(vrstica.skupni_saldo[k]))}>{vrstica.skupni_saldo[k]}</td>
              </tr>
            ))}
            {vrstica.dostava.drugo > 0 && (
              <tr className="border-b border-gray-50 dark:border-gray-800/60">
                <td className="py-1 text-gray-500">{VRSTA_LABEL.drugo}</td>
                <td className="py-1 text-right text-gray-600 dark:text-gray-300" colSpan={4}>
                  {vrstica.dostava.drugo_razpis && Object.keys(vrstica.dostava.drugo_razpis).length > 0
                    ? Object.entries(vrstica.dostava.drugo_razpis)
                        .sort((a, b) => a[0].localeCompare(b[0]))
                        .map(([k, v]) => k + ': ' + v).join(', ')
                    : vrstica.dostava.drugo}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// RAZCLENJENO: PDF + ena filiala
function VrsticaDetalj({ baza, vrstica, onSaved }:
  { baza: any; vrstica: Vrstica; onSaved: () => void }) {
  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-900/40 border-t-2 border-blue-400">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-sm text-gray-600 dark:text-gray-300">
          <span className="font-semibold text-gray-800 dark:text-gray-100">{vrstica.filiala}</span>
          <span className="ml-4"><span className="text-gray-400">Prevoznica:</span> {vrstica.st_prevoznice}</span>
          <span className="ml-4"><span className="text-gray-400">Voznik:</span> {vrstica.voznik}</span>
          <span className="ml-4"><span className="text-gray-400">Datum:</span> {fmtDatum(vrstica.datum)}</span>
        </div>
        <div className="flex items-center gap-2">
          <PotrdiPrevoznico baza={baza} transportId={vrstica.transport_id} status={vrstica.status} onDone={onSaved} />
          <IzbrisiPrevoznico baza={baza} transportId={vrstica.transport_id} stPrevoznice={vrstica.st_prevoznice} onDeleted={onSaved} />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SkenPanel baza={baza} transportId={vrstica.transport_id} />
        <div className="h-[60vh] overflow-auto">
          <FilialaUrejanje baza={baza} vrstica={vrstica} onSaved={onSaved} />
        </div>
      </div>
    </div>
  );
}

// PO PREVOZNICAH: PDF + vse filiale te prevoznice (urejljivo)
function PrevoznicaDetalj({ baza, vrstice, onSaved }:
  { baza: any; vrstice: Vrstica[]; onSaved: () => void }) {
  const t0 = vrstice[0];
  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-900/40 border-t-2 border-blue-400">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-sm text-gray-600 dark:text-gray-300">
          <span className="font-semibold text-gray-800 dark:text-gray-100">Prevoznica {t0.st_prevoznice}</span>
          <span className="ml-4"><span className="text-gray-400">Voznik:</span> {t0.voznik}</span>
          <span className="ml-4"><span className="text-gray-400">Datum:</span> {fmtDatum(t0.datum)}</span>
          <span className="ml-4"><span className="text-gray-400">Filiale:</span> {vrstice.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <PotrdiPrevoznico baza={baza} transportId={t0.transport_id} status={t0.status} onDone={onSaved} />
          <IzbrisiPrevoznico baza={baza} transportId={t0.transport_id} stPrevoznice={t0.st_prevoznice} onDeleted={onSaved} />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SkenPanel baza={baza} transportId={t0.transport_id} />
        <div className="h-[60vh] overflow-auto space-y-3">
          {vrstice.map((v) => (
            <FilialaUrejanje key={v.filiala} baza={baza} vrstica={v} onSaved={onSaved} prikaziNaslov />
          ))}
        </div>
      </div>
    </div>
  );
}

// trak napredka uvoza (na vrhu strani, kot pri prejetih racunih)
function UvozNapredek({ b, onClose }: { b: any; onClose: () => void }) {
  const skupaj = b.st_kompletov || 0;
  const obdelano = b.st_obdelano || 0;
  const koncano = b.status === 'koncano';
  const napaka = b.status === 'napaka';
  const pct = skupaj > 0 ? Math.round((obdelano / skupaj) * 100)
    : (koncano ? 100 : (b.status === 'v_vrsti' ? 0 : 5));
  return (
    <div className={clsx('rounded-lg border px-4 py-2.5 mb-3',
      napaka ? 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30'
        : koncano ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/30'
          : 'border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/30')}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2 text-sm font-medium min-w-0">
          {koncano ? <Check className="w-4 h-4 text-emerald-600 shrink-0" />
            : napaka ? <X className="w-4 h-4 text-red-600 shrink-0" />
              : <RefreshCw className="w-4 h-4 text-blue-600 animate-spin shrink-0" />}
          <span className={clsx('truncate',
            napaka ? 'text-red-700 dark:text-red-300'
              : koncano ? 'text-emerald-700 dark:text-emerald-300' : 'text-blue-700 dark:text-blue-300')}>
            {napaka ? 'Napaka pri uvozu'
              : koncano ? 'Uvoz končan'
                : (skupaj > 0 ? ('Uvažam ' + obdelano + ' / ' + skupaj + ' prevoznic') : 'Analiziram PDF …')}
          </span>
          {b.ime && <span className="text-xs text-gray-400 hidden md:inline truncate">· {b.ime}</span>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {!napaka && <span className="text-sm font-semibold tabular-nums text-gray-700 dark:text-gray-200">{pct}%</span>}
          {(koncano || napaka) && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {!napaka && (
        <div className="w-full bg-white/70 dark:bg-gray-800/70 rounded-full h-2 overflow-hidden">
          <div className={clsx('h-2 rounded-full transition-all duration-500',
            koncano ? 'bg-emerald-500' : 'bg-blue-500')} style={{ width: pct + '%' }} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs">
        {napaka ? (
          <span className="text-red-600 dark:text-red-400">{b.napaka || 'Neznana napaka'}</span>
        ) : (
          <>
            <span className="text-emerald-600 dark:text-emerald-400">Uvoženih: {b.st_uvozeno || 0}</span>
            {(b.st_obstaja || 0) > 0 && (
              <span className="text-gray-500">Podvojenih (preskočenih): {b.st_obstaja}</span>
            )}
            {(b.st_napak || 0) > 0 && <span className="text-red-500">Napak: {b.st_napak}</span>}
            {koncano && Array.isArray(b.opozorila) && b.opozorila.length > 0 && (
              <details className="text-amber-600 dark:text-amber-400">
                <summary className="cursor-pointer">{b.opozorila.length} opozoril</summary>
                <ul className="mt-1 space-y-0.5 max-h-32 overflow-auto list-disc list-inside text-gray-500 dark:text-gray-400">
                  {b.opozorila.map((o: string, i: number) => <li key={i}>{o}</li>)}
                </ul>
              </details>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// modul-level: zadnji uvozni batch prezivi menjavo baze/strani (SPA seja)
let _uvozBatchZadnji: any | null = null;

export function LogistikaPage() {
  const selectedCompany = useAuthStore((s: any) => s.selectedCompany);
  const baza = selectedCompany;
  const navigate = useNavigate();
  const [vrstice, setVrstice] = useState<Vrstica[]>([]);
  const [nalagam, setNalagam] = useState(false);
  const [uvazam, setUvazam] = useState(false);
  const [uvozBatch, setUvozBatch] = useState<any | null>(_uvozBatchZadnji);
  const nastaviUvozBatch = (b: any | null) => { _uvozBatchZadnji = b; setUvozBatch(b); };
  const [iskanje, setIskanje] = useState('');
  const [odprt, setOdprt] = useState<string | null>(null);
  const [pogled, setPogled] = useState<'razcleneno' | 'prevoznice'>('razcleneno');
  const [red, setRed] = useState<GrupaKljuc[]>(PRIVZETI_RED);
  const [datumMode, setDatumMode] = useState<DatumMode>('vse');
  const [datumOd, setDatumOd] = useState('');
  const [datumDo, setDatumDo] = useState('');
  const [saldoAgg, setSaldoAgg] = useState<{ soferji: Record<string, SaldoAgg>; filiale: Record<string, SaldoAgg> }>({ soferji: {}, filiale: {} });
  const fileRef = useRef<HTMLInputElement>(null);

  const naloziVrstice = useCallback(() => {
    if (!baza) { setVrstice([]); return; }
    setNalagam(true);
    api.get('/logistika/' + baza.id + '/vrstice')
      .then((r: any) => setVrstice(Array.isArray(r.data) ? r.data : []))
      .catch((err: any) => { if (err?.response?.status !== 403) toast.error('Napaka pri nalaganju'); })
      .finally(() => setNalagam(false));
  }, [baza]);
  useEffect(naloziVrstice, [naloziVrstice]);

  // v12: ob menjavi baze pocisti pogled in preveri modul (redirect, ce ni aktiven)
  useEffect(() => {
    setOdprt(null);
    setIskanje('');
    setVrstice([]);
    if (!baza) return;
    let ziv = true;
    api.get('/logistika/' + baza.id + '/aktiven')
      .then((r: any) => {
        if (!ziv) return;
        if (r.data && r.data.aktiven === false) {
          toast('To podjetje nima modula Logistika.', { icon: 'ℹ️' });
          navigate('/nadzorna'); // [PATCH aplikacije-fe v1]
        }
      })
      .catch(() => { });
    return () => { ziv = false; };
  }, [baza?.id]);

  useEffect(() => {
    api.get('/logistika/nastavitve/grupiranje')
      .then((r: any) => {
        const v = r.data?.vrednost;
        if (Array.isArray(v) && v.length === 3 &&
          v.every((x: any) => GRUPE.some((g) => g.k === x))) {
          setRed(v as GrupaKljuc[]);
        }
      })
      .catch(() => { });
    api.get('/logistika/nastavitve/pogled')
      .then((r: any) => {
        const v = r.data?.vrednost;
        if (v === 'razcleneno' || v === 'prevoznice') setPogled(v);
      })
      .catch(() => { });
  }, []);

  const shraniRed = (novi: GrupaKljuc[]) => {
    api.put('/logistika/nastavitve/grupiranje', { vrednost: novi }).catch(() => { });
  };

  const nastaviPogled = (p: 'razcleneno' | 'prevoznice') => {
    setPogled(p);
    setOdprt(null);
    api.put('/logistika/nastavitve/pogled', { vrednost: p }).catch(() => { });
  };

  const nastaviSlot = (i: number, val: GrupaKljuc) => {
    const novi = [...red];
    const j = novi.indexOf(val);
    if (j !== -1 && j !== i) novi[j] = novi[i];
    novi[i] = val;
    setRed(novi);
    shraniRed(novi);
  };

  const izberiDatoteko = () => fileRef.current?.click();

  const uvozi = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !baza) return;
    const fd = new FormData(); fd.append('file', file);
    setUvazam(true);
    api.post('/logistika/' + baza.id + '/uvozi-async', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r: any) => {
        const bid = r.data?.batch_id;
        if (bid) {
          nastaviUvozBatch({
            batch_id: bid, company_id: baza.id, status: 'v_vrsti', ime: file.name,
            st_kompletov: 0, st_obdelano: 0, st_uvozeno: 0, st_obstaja: 0,
            st_napak: 0, opozorila: [],
          });
        } else {
          toast.error('Uvoza ni bilo mogoče zagnati');
        }
      })
      .catch((err: any) => toast.error(String(err?.response?.data?.detail || 'Napaka pri zagonu uvoza')))
      .finally(() => { setUvazam(false); if (fileRef.current) fileRef.current.value = ''; });
  };

  // polling napredka uvoza - pripet na bazo, kjer se je uvoz zacel (v12)
  useEffect(() => {
    const bid = uvozBatch?.batch_id;
    const cid = uvozBatch?.company_id;
    if (!bid || !cid) return;
    if (uvozBatch.status === 'koncano' || uvozBatch.status === 'napaka') return;
    const ime = uvozBatch.ime;
    let ziv = true;
    const id = setInterval(() => {
      api.get('/logistika/' + cid + '/uvoz-batch/' + bid)
        .then((r: any) => {
          if (!ziv) return;
          nastaviUvozBatch({ ime, ...r.data, batch_id: bid, company_id: cid });
          if (r.data.status === 'koncano' && baza && baza.id === cid) naloziVrstice();
        })
        .catch(() => { });
    }, 1500);
    return () => { ziv = false; clearInterval(id); };
  }, [uvozBatch?.batch_id, uvozBatch?.status, uvozBatch?.company_id, baza?.id]);

  const q = iskanje.trim().toLowerCase();
  const obdobje = obdobjeZaMode(datumMode, datumOd, datumDo);

  const [izvazam, setIzvazam] = useState(false);
  const izvozExcel = () => {
    if (!baza || izvazam) return;
    let obm = obdobjeZaMode(datumMode, datumOd, datumDo);
    if (!obm) {
      // "Vse": od najstarejsega datuma v seznamu do danes
      const datumi = vrstice.map((v) => v.datum).filter(Boolean) as string[];
      if (datumi.length === 0) { toast.error('Ni podatkov za izvoz'); return; }
      obm = [datumi.reduce((a, b) => (a < b ? a : b)), isoDan(new Date())];
    }
    let [od, doo] = obm;
    if (od < '1900-01-01') od = '1900-01-01';
    if (doo > '9999-12-31') doo = '9999-12-31';
    setIzvazam(true);
    api.get('/logistika/' + baza.id + '/izvoz-saldo',
      { params: { od, do: doo }, responseType: 'blob' })
      .then((r: any) => {
        const url = URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Saldo_embalaze_' + od + '_' + doo + '.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Napaka pri izvozu v Excel'))
      .finally(() => setIzvazam(false));
  };
  const saldoOd = obdobje ? obdobje[0] : '2000-01-01';
  const saldoDo = obdobje ? obdobje[1] : isoDan(new Date());

  // saldo za izbrano obdobje (zacetno/promet/koncno) -> agregiran po soferju IN po filiali
  useEffect(() => {
    if (!baza) { setSaldoAgg({ soferji: {}, filiale: {} }); return; }
    api.get('/logistika/' + baza.id + '/saldo?od=' + saldoOd + '&do=' + saldoDo)
      .then((r: any) => {
        const vrst = r.data?.vrstice || [];
        const prazno = (): SaldoAgg => ({
          zacetno: { vozicki_rlc: 0, palete: 0, box: 0 },
          dostava: { vozicki_rlc: 0, palete: 0, box: 0 },
          vracilo: { vozicki_rlc: 0, palete: 0, box: 0 },
          koncno: { vozicki_rlc: 0, palete: 0, box: 0 },
        });
        const dodaj = (mapa: Record<string, SaldoAgg>, kljuc: string, s: any, vr: 'vozicki_rlc') => {
          if (!mapa[kljuc]) mapa[kljuc] = prazno();
          mapa[kljuc].zacetno[vr] += s.zacetno || 0;
          mapa[kljuc].dostava[vr] += s.dostavljeno || 0;
          mapa[kljuc].vracilo[vr] += s.vrnjeno || 0;
          mapa[kljuc].koncno[vr] += s.koncno || 0;
        };
        const soferji: Record<string, SaldoAgg> = {};
        const filiale: Record<string, SaldoAgg> = {};
        for (const s of vrst) {
          const vr = s.vrsta;
          if (vr !== 'vozicki_rlc' && vr !== 'palete' && vr !== 'box') continue;
          dodaj(soferji, s.voznik || '—', s, vr as 'vozicki_rlc');
          dodaj(filiale, s.filiala || '—', s, vr as 'vozicki_rlc');
        }
        setSaldoAgg({ soferji, filiale });
      })
      .catch(() => setSaldoAgg({ soferji: {}, filiale: {} }));
  }, [baza, saldoOd, saldoDo]);

  const filtrirane = vrstice.filter((v) => {
    if (q) {
      const najden = [v.st_prevoznice, v.voznik, v.filiala, v.reg_st, fmtDatum(v.datum)]
        .some((x: any) => String(x ?? '').toLowerCase().includes(q));
      if (!najden) return false;
    }
    if (obdobje) {
      if (!v.datum) return false;
      if (v.datum < obdobje[0] || v.datum > obdobje[1]) return false;
    }
    return true;
  });

  const sortirane = [...filtrirane].sort((a, b) => {
    for (const k of red) {
      const av = sortVrednost(a, k), bv = sortVrednost(b, k);
      if (av < bv) return -1;
      if (av > bv) return 1;
    }
    return 0;
  });

  const primarni = red[0];
  let prejsnjaGrupa: string | null = null;

  // po prevoznicah: grupiraj filtrirane vrstice po transportu
  const poPrevoznicah = (() => {
    const m: Record<string, Vrstica[]> = {};
    for (const v of filtrirane) { (m[v.transport_id] = m[v.transport_id] || []).push(v); }
    return Object.values(m).sort((a, b) =>
      (b[0].datum || '').localeCompare(a[0].datum || '') ||
      (a[0].st_prevoznice || '').localeCompare(b[0].st_prevoznice || ''));
  })();

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Truck className="w-6 h-6 text-blue-500" /> Logistika
        </h1>
        {!baza && <span className="text-sm text-gray-400">Izberite podjetje v glavi zgoraj.</span>}
      </div>

      {baza && (
        <>
          {uvozBatch && baza && uvozBatch.company_id === baza.id && (
            <UvozNapredek b={uvozBatch} onClose={() => nastaviUvozBatch(null)} />
          )}
          <div className="flex items-center gap-2 mb-3">
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
              <button onClick={() => nastaviPogled('prevoznice')}
                className={clsx('px-3 py-1.5 text-sm font-medium',
                  pogled === 'prevoznice' ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700')}>
                Po prevoznicah
              </button>
              <button onClick={() => nastaviPogled('razcleneno')}
                className={clsx('px-3 py-1.5 text-sm font-medium border-l border-gray-300 dark:border-gray-600',
                  pogled === 'razcleneno' ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700')}>
                Razčlenjeno
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button onClick={izberiDatoteko} disabled={uvazam}
              className={clsx('px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all',
                uvazam ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700')}>
              <Upload className={clsx('w-4 h-4', uvazam && 'animate-pulse')} />
              {uvazam ? 'Uvažam …' : 'Uvozi prevoznice (PDF)'}
            </button>
            <input ref={fileRef} type="file" accept="application/pdf" onChange={uvozi} className="hidden" />
            <input value={iskanje} onChange={(e) => setIskanje(e.target.value)}
              placeholder="Iskanje po št. prevoznice, vozniku, filiali …"
              className="flex-1 min-w-[200px] px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm" />
            <button onClick={naloziVrstice} title="Osveži"
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
              <RefreshCw className={clsx('w-4 h-4', nalagam && 'animate-spin')} />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
            <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <Calendar className="w-4 h-4" /> Obdobje:
            </span>
            {(([['vse', 'Vse'], ['tekoci', 'Tekoči mesec'], ['zadnji', 'Zadnji mesec'], ['po_meri', 'Po meri']]) as [DatumMode, string][]).map(([m, label]) => (
              <button key={m} onClick={() => setDatumMode(m)}
                className={clsx('px-2.5 py-1 rounded-lg text-sm border',
                  datumMode === m ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400')}>
                {label}
              </button>
            ))}
            {datumMode === 'po_meri' && (
              <span className="flex items-center gap-1">
                <input type="date" value={datumOd} onChange={(e) => setDatumOd(e.target.value)}
                  className="px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm" />
                <span className="text-gray-400">–</span>
                <input type="date" value={datumDo} onChange={(e) => setDatumDo(e.target.value)}
                  className="px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm" />
              </span>
            )}
            <button onClick={izvozExcel} disabled={izvazam}
              title="Excel: saldo za izbrano obdobje + podrobnosti prevoznic"
              className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-lg border text-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/40 disabled:opacity-50">
              <Download className={clsx('w-4 h-4', izvazam && 'animate-pulse')} />
              {izvazam ? 'Izvažam …' : 'Izvoz v Excel'}
            </button>
          </div>

          {pogled === 'razcleneno' && (
            <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
              <span className="text-gray-500 dark:text-gray-400">Razvrsti po:</span>
              {[0, 1, 2].map((i) => (
                <span key={i} className="flex items-center gap-1">
                  <span className="text-xs text-gray-400">{i + 1}.</span>
                  <select value={red[i]} onChange={(e) => nastaviSlot(i, e.target.value as GrupaKljuc)}
                    className="px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm">
                    {GRUPE.map((g) => <option key={g.k} value={g.k}>{g.label}</option>)}
                  </select>
                </span>
              ))}
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-auto">
          {pogled === 'razcleneno' ? (
            <table className="w-full text-sm">
              <thead className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-3 py-3 text-left whitespace-nowrap">Št. prevoznice</th>
                  <th className="px-3 py-3 text-left whitespace-nowrap">Datum</th>
                  <th className="px-3 py-3 text-left">Voznik</th>
                  <th className="px-3 py-3 text-left">Filiala</th>
                  <th className="px-3 py-3 text-left whitespace-nowrap">Skupni saldo (V/P/B)</th>
                  <th className="px-3 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {sortirane.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                    {nalagam ? 'Nalagam …' : (iskanje ? 'Ni zadetkov.' : 'Ni podatkov. Naložite PDF s prevoznicami.')}
                  </td></tr>
                )}
                {sortirane.map((v) => {
                  const kljuc = kljucVrstice(v);
                  const jeOdprt = odprt === kljuc;
                  const grupa = grupaLabel(v, primarni);
                  const novaGrupa = grupa !== prejsnjaGrupa;
                  prejsnjaGrupa = grupa;
                  const grupaNaziv = GRUPE.find((g) => g.k === primarni)?.label || '';
                  return (
                    <React.Fragment key={kljuc}>
                      {novaGrupa && (
                        <>
                          <tr className="bg-gray-100/80 dark:bg-gray-900/60">
                            <td colSpan={6} className="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">
                              {grupaNaziv}: {grupa}
                            </td>
                          </tr>
                          {primarni === 'voznik' && saldoAgg.soferji[v.voznik || '—'] && (
                            <tr className="bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
                              <td colSpan={6} className="px-3 py-2">
                                <SaldoPovzetek agg={saldoAgg.soferji[v.voznik || '—']}
                                  naslov="Saldo šoferja (izbrano obdobje):" />
                              </td>
                            </tr>
                          )}
                          {primarni === 'filiala' && saldoAgg.filiale[v.filiala] && (
                            <tr className="bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
                              <td colSpan={6} className="px-3 py-2">
                                <SaldoPovzetek agg={saldoAgg.filiale[v.filiala]}
                                  naslov="Saldo filiale (izbrano obdobje):" />
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                      <tr onClick={() => setOdprt(jeOdprt ? null : kljuc)}
                        className={clsx('border-b border-gray-100 dark:border-gray-700 cursor-pointer',
                          jeOdprt ? 'bg-blue-50/60 dark:bg-blue-950/30' : 'hover:bg-gray-50 dark:hover:bg-gray-900/40')}>
                        <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                          <span className="flex items-center gap-1.5">
                            <ChevronDown className={clsx('w-4 h-4 text-gray-400 transition-transform',
                              jeOdprt ? 'rotate-0' : '-rotate-90')} />
                            {v.st_prevoznice}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtDatum(v.datum)}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{v.voznik || '—'}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">{v.filiala}</td>
                        <td className="px-3 py-2"><SaldoMini s={v.skupni_saldo} /></td>
                        <td className="px-3 py-2">
                          <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', statusCls(v.status || ''))}>
                            {v.status || '—'}
                          </span>
                        </td>
                      </tr>
                      {jeOdprt && (
                        <tr>
                          <td colSpan={6} className="p-0">
                            <VrsticaDetalj baza={baza} vrstica={v} onSaved={naloziVrstice} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-3 py-3 text-left whitespace-nowrap">Št. prevoznice</th>
                  <th className="px-3 py-3 text-left whitespace-nowrap">Datum</th>
                  <th className="px-3 py-3 text-left">Voznik</th>
                  <th className="px-3 py-3 text-left whitespace-nowrap">Reg. št.</th>
                  <th className="px-3 py-3 text-center whitespace-nowrap">Filiale</th>
                  <th className="px-3 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {poPrevoznicah.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                    {nalagam ? 'Nalagam …' : (iskanje ? 'Ni zadetkov.' : 'Ni podatkov. Naložite PDF s prevoznicami.')}
                  </td></tr>
                )}
                {poPrevoznicah.map((grp) => {
                  const t0 = grp[0];
                  const jeOdprt = odprt === t0.transport_id;
                  return (
                    <React.Fragment key={t0.transport_id}>
                      <tr onClick={() => setOdprt(jeOdprt ? null : t0.transport_id)}
                        className={clsx('border-b border-gray-100 dark:border-gray-700 cursor-pointer',
                          jeOdprt ? 'bg-blue-50/60 dark:bg-blue-950/30' : 'hover:bg-gray-50 dark:hover:bg-gray-900/40')}>
                        <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                          <span className="flex items-center gap-1.5">
                            <ChevronDown className={clsx('w-4 h-4 text-gray-400 transition-transform',
                              jeOdprt ? 'rotate-0' : '-rotate-90')} />
                            {t0.st_prevoznice}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtDatum(t0.datum)}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{t0.voznik || '—'}</td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">{t0.reg_st || '—'}</td>
                        <td className="px-3 py-2 text-center">
                          <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300">
                            <Package className="w-3.5 h-3.5 text-gray-400" />{grp.length}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', statusCls(t0.status || ''))}>
                            {t0.status || '—'}
                          </span>
                        </td>
                      </tr>
                      {jeOdprt && (
                        <tr>
                          <td colSpan={6} className="p-0">
                            <PrevoznicaDetalj baza={baza} vrstice={grp} onSaved={naloziVrstice} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
          </div>

          <div className="mt-2 text-xs text-gray-400">
            {pogled === 'razcleneno'
              ? (iskanje ? sortirane.length + ' od ' + vrstice.length : vrstice.length) + ' vrstic (transport × filiala)'
              : poPrevoznicah.length + ' prevoznic'}
          </div>
        </>
      )}
    </div>
  );
}
